import { pagedList } from './pagedList';
import { del, get, searchAllPages, post } from '@/lib/apiClient';
import { endpoints } from './endpoints';
import { schoolScope } from './schoolScope';
import type {
  ParentInfo,
  Student,
  StudentAddress,
  StudentAddressInput,
  StudentAdmission,
  StudentContact,
  StudentDetails,
  StudentDetailsInput,
  StudentImportCheck,
  StudentImportResult,
  StudentImportRow,
  StudentListQuery,
  StudentParent,
  StudentPreviousSchool,
} from '@/models';

const f = endpoints.foundation;
const legacy = endpoints.students;

const fromApiCode = (value: string | null | undefined) =>
  value ? value.toLowerCase().replaceAll('_', '') : null;

const toApiCode = (value: string | null | undefined) =>
  value ? value.replaceAll(' ', '_').toUpperCase() : null;

const fromResidence = (value: string | null | undefined) =>
  value === 'HOSTELLER' ? 'hostler' : value === 'DAY_SCHOLAR' ? 'dayscholar' : fromApiCode(value);

/** Students saved before the name was split have only the full name: first word, last word, the rest. */
const nameParts = (student: Student) => {
  if (student.firstName) return {};
  const words = (student.studentName ?? '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: words[0] ?? null,
    lastName: words.length > 1 ? words.slice(1).join(' ') : null,
  };
};

const toStudent = (student: Student): Student => ({
  ...student,
  ...nameParts(student),
  gender: fromApiCode(student.gender),
  hostlerDayscholar: fromResidence(student.hostlerDayscholar),
});

const toStudentInput = (student: Partial<Student>) => ({
  firstName: student.firstName,
  lastName: student.lastName,
  admissionNo: student.admissionNo,
  registerNo: student.registerNo,
  mobile: student.mobile?.trim() || null,
  email: student.email?.trim() || null,
  // Not stored on the student: the API enrolls (or moves) the student in the current academic year.
  classId: student.classId || null,
  sectionId: student.sectionId || null,
  // Always sent: on update null clears the house.
  houseId: student.houseId || null,
  dob: student.dob,
  gender: toApiCode(student.gender),
  bloodGroup: student.bloodGroup || null,
  identification1: student.identification1,
  identification2: student.identification2,
  motherTongue: student.motherTongue,
  nationality: student.nationality,
  religion: student.religion,
  hostlerDayscholar:
    student.hostlerDayscholar === 'hostler'
      ? 'HOSTELLER'
      : student.hostlerDayscholar === 'dayscholar'
        ? 'DAY_SCHOLAR'
        : toApiCode(student.hostlerDayscholar),
  aadharNo: student.aadharNo,
  incomeCertificate: student.incomeCertificate,
  photo: student.photo,
  category: student.category || null,
  mediumOfInstruction: student.mediumOfInstruction,
  secondLanguage: student.secondLanguage,
  transportRequired: student.transportRequired ?? false,
  specialNeeds: student.specialNeeds,
  siblingDetails: student.siblingDetails,
});

export const listStudents = async (schoolId: number, query: StudentListQuery, signal?: AbortSignal) =>
  (
    await searchAllPages<Student>(
      f.students(),
      {
        classId: query.classId ?? undefined,
        sectionId: query.sectionId ?? undefined,
        query: query.query ?? undefined,
      },
      { ...(await schoolScope(schoolId)), signal },
    )
  ).map(toStudent);

export const createStudent = async (schoolId: number, student: Partial<Student>) =>
  toStudent(
    await post<Student>(
      f.students(),
      // Create only: the API assigns the category from the admission date.
      { ...toStudentInput(student), studentCategoryId: student.studentCategoryId || null },
      await schoolScope(schoolId),
    ),
  );

/** The import request body: the student's fields at the top level of `student`, the details as lists. */
const toImportInput = ({ student, enrollment, details }: StudentImportRow) => ({
  student: toStudentInput(student),
  // Each id wins over its name on the server; the names stay for compatibility.
  academicYearId: enrollment.academicYearId ?? null,
  classId: enrollment.classId ?? null,
  sectionId: enrollment.sectionId ?? null,
  academicYear: enrollment.academicYear || null,
  className: enrollment.className,
  sectionName: enrollment.sectionName,
  rollNo: enrollment.rollNo || null,
  enrolledDate: enrollment.enrolledDate || null,
  addresses: [details.address, details.permanentAddress].filter((address) => address !== null),
  parents: details.parents,
  previousSchool: details.previousSchool,
});

/**
 * Creates the student, their enrollment in the named year, class and section, and their details
 * in one save: if any part fails nothing is created, so the row can be fixed and sent again.
 */
export const importStudent = async (schoolId: number, row: StudentImportRow, signal?: AbortSignal) => {
  const result = await post<StudentImportResult>(f.studentImport(), toImportInput(row), {
    ...(await schoolScope(schoolId)),
    signal,
  });
  return { ...result, student: toStudent(result.student) };
};

/** Checks import rows the way `importStudent` would, without saving anything. */
export const validateStudentImport = async (schoolId: number, rows: StudentImportRow[], signal?: AbortSignal) =>
  post<StudentImportCheck[]>(f.studentImportValidate(), rows.map(toImportInput), {
    ...(await schoolScope(schoolId)),
    signal,
  });

export const getStudent = async (schoolId: number, studentId: number) =>
  toStudent(await get<Student>(f.student(studentId), await schoolScope(schoolId)));

export const updateStudent = async (schoolId: number, studentId: number, student: Partial<Student>) =>
  toStudent(await post<Student>(f.student(studentId), toStudentInput(student), await schoolScope(schoolId)));

export const deleteStudent = async (schoolId: number, studentId: number) =>
  del<unknown>(f.student(studentId), await schoolScope(schoolId));

export const getStudentDetails = async (
  schoolId: number,
  studentId: number,
  signal?: AbortSignal,
): Promise<StudentDetails> => {
  const config = { ...(await schoolScope(schoolId)), signal };
  const [addresses, parents, previousSchools] = await Promise.all([
    searchAllPages<StudentAddress>(f.studentAddresses(studentId), {}, config),
    searchAllPages<StudentParent>(f.studentParents(studentId), {}, config),
    searchAllPages<StudentPreviousSchool>(f.studentPreviousSchools(studentId), {}, config),
  ]);
  return { addresses, parents, previousSchools };
};

/**
 * Makes the stored records match what the form holds: a record is created,
 * updated or (when its form section was cleared) deleted. The form holds one
 * address, one record per relation and one previous school, so only the
 * primary address, the record of each relation and the first previous school
 * are touched.
 */
export const saveStudentDetails = async (
  schoolId: number,
  studentId: number,
  input: StudentDetailsInput,
): Promise<void> => {
  const existing = await getStudentDetails(schoolId, studentId);
  const scope = await schoolScope(schoolId);

  const saveAddress = async (stored: StudentAddress | undefined, values: StudentAddressInput | null) => {
    if (values) {
      if (stored)
        await post<unknown>(f.studentAddress(studentId, stored.studentAddressId), values, scope);
      else await post<unknown>(f.studentAddresses(studentId), values, scope);
    } else if (stored) {
      await del<unknown>(f.studentAddress(studentId, stored.studentAddressId), scope);
    }
  };
  // Permanent first, so a current address saved after it ends up the primary one.
  await saveAddress(existing.addresses.find((a) => a.addressType === 'PERMANENT'), input.permanentAddress);
  await saveAddress(
    existing.addresses.find((a) => a.addressType !== 'PERMANENT'),
    input.address,
  );

  for (const relation of ['FATHER', 'MOTHER', 'GUARDIAN'] as const) {
    const parent = existing.parents.find((p) => p.relation === relation);
    const values = input.parents.find((p) => p.relation === relation);
    if (values) {
      if (parent) await post<unknown>(f.studentParent(studentId, parent.parentId), values, scope);
      else await post<unknown>(f.studentParents(studentId), values, scope);
    } else if (parent) {
      await del<unknown>(f.studentParent(studentId, parent.parentId), scope);
    }
  }

  const previousSchool = existing.previousSchools[0];
  if (input.previousSchool) {
    if (previousSchool)
      await post<unknown>(
        f.studentPreviousSchool(studentId, previousSchool.previousSchoolId),
        input.previousSchool,
        scope,
      );
    else await post<unknown>(f.studentPreviousSchools(studentId), input.previousSchool, scope);
  } else if (previousSchool) {
    await del<unknown>(f.studentPreviousSchool(studentId, previousSchool.previousSchoolId), scope);
  }
};

/** The admission the student was enrolled from, with every step it went through. */
export const getStudentAdmission = async (schoolId: number, studentId: number) =>
  get<StudentAdmission>(f.studentAdmission(studentId), await schoolScope(schoolId));

// The legacy mock endpoints still back the student and parent portals.
export const getStudentContact = (contactId: number) =>
  post<StudentContact[]>(legacy.contacts, { contactId });
export const getParentInfo = (parentId: number) =>
  post<ParentInfo[]>(legacy.parentInfo, { parentId });

// Server-driven table lists.
export const listStudentsPage = pagedList<Student, ReturnType<typeof toStudent>>(f.students(), toStudent);
